import type {
  NegativeProductivityOptionId,
  SelectableItem,
} from '@physitrack/championhealth-types';

import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Checkbox } from '../questions/checkbox/Checkbox';
import { Clock } from '../questions/clock/Clock';
import { OneToTenSlider } from '../questions/painSlider/OneToTenSlider';
import { AssessmentPageItem } from '../types/AssessmentPageItem';

export function Energy1({
  assessmentValues,
  questionid,
  questionHeading,
  questionText,
  onOpenModal,
  onSetValue,
}: AssessmentPageItem) {
  const { t } = useTranslation();
  const productivityTextValues = [t('EN045'), t('EN046')];

  const productivityOptions: SelectableItem<NegativeProductivityOptionId>[] = [
    { id: 'none', title: t('EN001') },
    { id: 'tiredness', title: t('EN002') },
    { id: 'high_stress', title: t('EN003') },
    { id: 'poor_nutrition', title: t('EN004') },
    { id: 'dehydration', title: t('EN005') },
    { id: 'mental_health', title: t('EN006') },
    { id: 'headaches', title: t('EN007') },
    { id: 'inactivity', title: t('EN008') },
  ];

  if (assessmentValues.current_joint_muscle_pain === 'yes' && assessmentValues.areas_of_pain) {
    for (let i = 0; i < assessmentValues.areas_of_pain.length; i++) {
      productivityOptions.push({
        id: assessmentValues.areas_of_pain[i].id,
        title: assessmentValues.areas_of_pain[i].title + t('EN009'),
      });
    }
  }

  if (assessmentValues.tailor_recommendations === 'female_specific') {
    productivityOptions.push({
      id: 'female_health',
      title: t('EN010'),
    });
  }

  if (assessmentValues.drink_alcohol !== 'never') {
    productivityOptions.push({
      id: 'alcohol',
      title: t('EN011'),
    });
  }

  if (assessmentValues.smoker === 'yes') {
    productivityOptions.push({
      id: 'smoking',
      title: t('EN012'),
    });
  }

  if (assessmentValues.long_term_conditions) {
    for (let i = 0; i < assessmentValues.long_term_conditions.length; i++) {
      if (assessmentValues.long_term_conditions[i].id !== 'none') {
        productivityOptions.push({
          id: assessmentValues.long_term_conditions[i].id,
          title: assessmentValues.long_term_conditions[i].title,
        });
      }
    }
  }
  /* other last */
  if (productivityOptions.filter((e) => e.id === 'other').length === 0) {
    productivityOptions.push({ id: 'other', title: t('EN013') });
  }

  const impactTextValues = [t('EN014'), t('EN015')];

  useEffect(() => {
    if (
      (assessmentValues.negative_productivity &&
        assessmentValues.negative_productivity.length > 0 &&
        assessmentValues.negative_productivity[0]?.id === 'none') ||
      !assessmentValues.negative_productivity
    ) {
      onSetValue({ id: 'productivity_last_7', value: null });
    }
  }, [assessmentValues.negative_productivity]);

  return (
    <div className="question_container" id={questionid}>
      <h1>{questionHeading}</h1>
      <p>{questionText}</p>
      <OneToTenSlider
        questionid="energy_level"
        questionTitle={t('assessment.question.energy_level')}
        width="_wide"
        textValues={[t('EN017'), t('EN018')]}
        onSetValue={onSetValue}
        assessmentValues={assessmentValues}
      />
      <fieldset id="energy_info" data-input-type="energy_info">
        <legend className="assessment_question_title">{t('EN019')}</legend>
        <hr />
        <div className="energy_info">
          <Clock
            questionid="most_energetic"
            width=""
            questionTitle={t('assessment.question.most_energetic')}
            startHour={9}
            startMinute={0}
            onSetValue={onSetValue}
            assessmentValues={assessmentValues}
          />
          <Clock
            questionid="least_energetic"
            width=""
            questionTitle={t('assessment.question.least_energetic')}
            startHour={17}
            startMinute={0}
            onSetValue={onSetValue}
            assessmentValues={assessmentValues}
          />
        </div>
      </fieldset>
      <OneToTenSlider
        questionid="productivity"
        questionTitle={t('assessment.question.productivity')}
        width="_wide"
        textValues={productivityTextValues}
        onSetValue={onSetValue}
        assessmentValues={assessmentValues}
        modalText={t('EN023')}
        onOpenModal={onOpenModal}
      />
      <Checkbox
        questionid="negative_productivity"
        width="_wide"
        questionTitle={t('assessment.question.negative_productivity')}
        questionTitleSuffix={t('EN025')}
        options={productivityOptions}
        itemLimit="2"
        onSetValue={onSetValue}
        assessmentValues={assessmentValues}
      />
      {assessmentValues.negative_productivity &&
      assessmentValues.negative_productivity.length > 0 &&
      assessmentValues.negative_productivity[0]?.id !== 'none' ? (
        <OneToTenSlider
          questionid="productivity_last_7"
          questionTitle={t('assessment.question.productivity_last_7')}
          width="_wide"
          textValues={impactTextValues}
          onSetValue={onSetValue}
          assessmentValues={assessmentValues}
          modalText={t('EN028')}
          onOpenModal={onOpenModal}
        />
      ) : null}
    </div>
  );
}
